// One row of a catalog in its own drawer (DD-045, TD-099): a work type, a service line or an expense category. Rename it,
// or delete it while nothing uses it, confirmed in the footer, never in a second drawer. Given a list behind it, Back, a
// save and a delete hand back to it in place of this drawer. Without one there is no Back, and a save or a delete closes
// (slice 10 R15). X and Escape close it, and any list behind it. The name is checked as the server checks it for all
// three (labelCheck), and Enter saves.
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import { LABEL_MAX, labelCheck, type NamedRow } from "../utils/catalogs";
import { closeUnlessPending } from "../utils/mutations";
import { FinanceDrawer } from "./FinanceDrawer";

export type CatalogRow = NamedRow & { inUse: boolean };

interface Props {
  row: CatalogRow;
  /** Every row of its catalog: no other may have the new name, in any case. */
  rows: CatalogRow[];
  /** What the row is, capitalised: "Work type", "Service line", "Category". */
  noun: string;
  /** Under the name while something uses the row, so it stays. */
  inUseNote: string;
  /** The delete confirm's question. */
  deleteQuestion: string;
  rename: { send: (label: string) => Promise<unknown>; pending: boolean };
  remove: { send: () => Promise<unknown>; pending: boolean };
  /** Back to the list this row opened from, replacing it in the history, and where a save or a delete hands back. With
   * none (a row opened from a panel, as Setup's categories are) there is no Back, and a save or a delete closes (J26). */
  onBack?: () => void;
  /** Closes the row and the list. */
  onClose: () => void;
  /** Fields under the name, e.g. a service line's split defaults (TD-078). */
  children?: ReactNode;
  /** Those fields hold Save, e.g. a split over 100%. */
  blocked?: boolean;
  /** Those fields changed: Save sends with the name as it was. */
  dirty?: boolean;
  /** Wider for a split editor. */
  size?: "md" | "lg";
}

export function CatalogRowForm({ row, rows, noun, inUseNote, deleteQuestion, rename, remove, onBack, onClose, children, blocked, dirty, size = "md" }: Props) {
  const [typed, setTyped] = useState(row.label);
  const footerStep = useFooterStep<"delete">(null);
  const pending = rename.pending || remove.pending;
  const check = labelCheck(noun.toLowerCase(), rows, row, typed);
  // The name rules always hold. An unchanged name holds Save unless the fields under it changed.
  const held = check.error !== null || (check.label === row.label && !dirty) || !!blocked;
  const send = useSubmitOnce();
  // A refusal (toasted by the mutation, the data refetched) clears the delete confirm, so it never offers itself again.
  const settle = (request: () => Promise<unknown>) => send(request, () => (onBack ?? onClose)(), footerStep.leave);
  const save = () => settle(() => rename.send(check.label));
  const doDelete = () => settle(remove.send);
  const deleteLabel = `Delete ${noun.toLowerCase()}`;

  // The confirm goes with the row's delete once something uses it (a refetch).
  const footer = footerStep.step === "delete" && !row.inUse ? (
    // Stacked: at max-w-md the sentence beside two buttons gets about 150px.
    <FooterConfirm confirmLabel={deleteLabel} onConfirm={doDelete} onKeep={footerStep.leave} pending={pending} loading={remove.pending} stacked>
      {deleteQuestion}
    </FooterConfirm>
  ) : (
    <>
      {!row.inUse && (
        <Button
          variant="ghost"
          className="mr-auto whitespace-nowrap text-red-600 hover:text-red-700"
          onClick={() => footerStep.ask("delete")}
          disabled={pending}
          autoFocus={footerStep.returnsTo("delete")}
        >
          {deleteLabel}
        </Button>
      )}
      {onBack && <Button variant="secondary" onClick={onBack} disabled={pending}>Back</Button>}
      <Button onClick={save} disabled={held || pending} isLoading={rename.pending}>Save</Button>
    </>
  );

  return (
    <FinanceDrawer onClose={closeUnlessPending(pending, onClose)} size={size} title={row.label} footer={footer}>
      <div className="space-y-3">
        <FormField label="Name" error={check.error}>
          <input
            className={inputClassName}
            aria-label={`${noun} name`}
            value={typed}
            maxLength={LABEL_MAX + 20}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !held) save();
            }}
            autoFocus
          />
        </FormField>
        {row.inUse && <p className="text-xs text-gray-500">{inUseNote}</p>}
        {children}
      </div>
    </FinanceDrawer>
  );
}
